import React, { useEffect } from "react";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Formik, Field, FormikProps } from "formik";
import * as Yup from "yup";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { createRole, updateRole } from "../redux/actions/roleActions";
import { getPermissions } from "../redux/actions/permissionActions";
import { cleanFormData } from "../helpers/formHelpers";

import { RoleResponseDto } from "../shared/dtos/role/RoleResponseDto";
import { RoleRequestDto } from "../shared/dtos/role/RoleRequestDto";
import { PermissionDto } from "../shared/dtos/permission/PermissionDto";
import { roleActions } from "../redux/slices/role";

const roleValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Role name is required")
    .matches(
      /^[A-Z_]*$/,
      "Role name must be uppercase with underscores (e.g., SUPER_ADMIN)",
    ),
  description: Yup.string().required("Description is required"),
  permissions: Yup.array().of(Yup.string().required()),
});

interface GroupedPermissions {
  [group: string]: string[];
}

const groupPermissions = (
  permissionList: PermissionDto[],
): GroupedPermissions => {
  return permissionList.reduce((acc: GroupedPermissions, permissionObj) => {
    const permission = permissionObj.name;
    const [group] = permission.split(":");

    if (!acc[group]) {
      acc[group] = [];
    }

    acc[group].push(permission);
    return acc;
  }, {});
};

interface CreateOrUpdateRoleFormProps {
  role?: RoleResponseDto | null;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateRoleForm: React.FC<CreateOrUpdateRoleFormProps> = ({
  role = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();
  const { items: permissions, loading: loadingPermissions } = useAppSelector(
    (state) => state.permission,
  );

  useEffect(() => {
    if (isEditing && permissions.length === 0) {
      dispatch(getPermissions());
    }
  }, [dispatch, isEditing, permissions.length]);

  const initialValues = {
    name: role?.name || "",
    description: role?.description || "",
    permissions: role?.permissions || [],
  };

  const grouped = groupPermissions(permissions);

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(roleActions.setErrorUpdate(null))
            : () => dispatch(roleActions.setErrorCreate(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={roleValidationSchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values) as RoleRequestDto;
          if (isEditing && role) {
            dispatch(updateRole({ id: role.id, values: cleanedValues }));
          } else {
            dispatch(createRole(cleanedValues));
          }
        }}
        enableReinitialize={true}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
        }: FormikProps<typeof initialValues>) => (
          <form onSubmit={handleSubmit}>
            <FormTextField
              name="name"
              label="Role Name (e.g., SUPER_ADMIN)"
              disabled={isEditing}
            />

            <FormTextField
              name="description"
              label="Description"
              multiline
              rows={2}
            />

            {isEditing && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Permissions
                </Typography>
                {loadingPermissions ? (
                  <CircularProgress />
                ) : (
                  <Grid
                    container
                    spacing={2}
                    sx={{ maxHeight: "40vh", overflowY: "auto", p: 1 }}
                  >
                    {Object.keys(grouped)
                      .sort()
                      .map((groupName) => (
                        <Box
                          key={groupName}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 2,
                            height: "100%",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            component="div"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {groupName}
                          </Typography>
                          <FormGroup>
                            {grouped[groupName].sort().map((permission) => (
                              <FormControlLabel
                                key={permission}
                                control={
                                  <Field
                                    as={Checkbox}
                                    type="checkbox"
                                    name="permissions"
                                    value={permission}
                                    checked={values.permissions.includes(
                                      permission,
                                    )}
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      const newPermissions = e.target.checked
                                        ? [...values.permissions, permission]
                                        : values.permissions.filter(
                                            (p) => p !== permission,
                                          );
                                      setFieldValue(
                                        "permissions",
                                        newPermissions,
                                      );
                                    }}
                                  />
                                }
                                label={permission.split(":")[1] || permission}
                              />
                            ))}
                          </FormGroup>
                        </Box>
                      ))}
                  </Grid>
                )}
              </Box>
            )}

            <Box sx={{ mt: 3 }}>
              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Role"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
              />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateRoleForm;
